import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { CancelConfirmationModal } from '@dealstack/frontend/shared/components/CancelConfirmationModal/CancelConfirmationModal';
import { PhoneNumberInput } from '@dealstack/frontend/shared/components/forms/PhoneNumberInput/PhoneNumberInput';
import { StringInput as StringInputV2 } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import {
	useTranslationCommonUi,
	useTranslationUserProfiles,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { EMPTY_CALLBACK } from '@dealstack/shared/lib/utils';
import { Button, Flex, Grid, Text as MantineText } from '@mantine/core';
import { useCallback, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';
import {
	PERSONAL_INFO_FORM,
	useManagePersonalDetailsController,
} from '@dealstack/frontend/shared/components/Profile/PersonalDetails/ManagePersonalDetails/useManagePersonalDetailsController';
import { Select } from 'react-hook-form-mantine';
import { COUNTRIES_GROUPED_OPTIONS } from '@dealstack/shared/lib/countries/countriesData';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import { AddSignatureModal } from '@dealstack/frontend/shared/components/Signature/AddSignatureModal/AddSignatureModal';
import { PngPreview } from '@dealstack/frontend/shared/components/Signature/PngPreview/PngPreview';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

interface Props {
	className?: string;
}

const ManagePersonalDetails = ({ className }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const tUserProfiles = useTranslationUserProfiles();
	const {
		isEditModeEnabled,
		personalDetailsForm,
		onSubmit,
		setIsEditModeEnabled,
		showModal,
		setShowModal,
		isLoading,
		changeToViewMode,
		user,
		firstPerson,
		isDirty,
		showSignatureModal,
		setShowSignatureModal,
		savedSignature,
		deleteSignature,
	} = useManagePersonalDetailsController();

	const { handleSubmit } = personalDetailsForm;

	const FIELD_NAME = useMemo(
		() => ({
			[PERSONAL_INFO_FORM.FIRST_NAME]: tUserProfiles('personal_details.first_name'),
			[PERSONAL_INFO_FORM.LAST_NAME]: tUserProfiles('personal_details.last_name'),
			[PERSONAL_INFO_FORM.BIRTH_DATE]: tUserProfiles('personal_details.date_of_birth'),
			[PERSONAL_INFO_FORM.PHONE_NUMBER]: tUserProfiles('personal_details.phone_number'),
			[PERSONAL_INFO_FORM.HOUSE_NUMBER]: tUserProfiles('personal_details.house_number'),
			[PERSONAL_INFO_FORM.STREET]: tUserProfiles('personal_details.street'),
			[PERSONAL_INFO_FORM.CITY]: tUserProfiles('personal_details.city'),
			[PERSONAL_INFO_FORM.COUNTRY]: tUserProfiles('personal_details.country'),
			[PERSONAL_INFO_FORM.POST_CODE]: tUserProfiles('personal_details.postal_code'),
		}),
		[]
	);

	const isSignatureImageTurnedOn = useDealstackFeatureFlag(
		FEATURE_FLAG.USER_PROFILE_SIGNATURE_IMAGE
	).value;

	const renderPlaceHolderText = useCallback(
		(fieldName: string) =>
			tUserProfiles('placeholder_text', {
				field_name: fieldName.toLowerCase(),
			}),
		[]
	);

	if (isLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (!user || !firstPerson) {
		return <span>{tUserProfiles('error.no_user')}</span>;
	}

	return (
		<div className={className}>
			<FormProvider {...personalDetailsForm}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<PersonalInputsWrapper>
						<Grid gutter={50}>
							<Grid.Col span={4}>
								<PersonalInformationWrapper>
									<Text variant="sans/11/800" disableVariantMapping>
										{tUserProfiles('personal_details.personal_information')}
									</Text>

									<StringInputV2
										label={FIELD_NAME.firstName}
										readOnly={!isEditModeEnabled}
										name={PERSONAL_INFO_FORM.FIRST_NAME}
										inputPlaceHolder={renderPlaceHolderText(
											FIELD_NAME.firstName
										)}
										onInputChange={EMPTY_CALLBACK}
										size="m"
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_FIRST_NAME
										}
									/>

									<StringInputV2
										label={FIELD_NAME.lastName}
										readOnly={!isEditModeEnabled}
										name={PERSONAL_INFO_FORM.LAST_NAME}
										inputType="text"
										inputPlaceHolder={renderPlaceHolderText(
											FIELD_NAME.lastName
										)}
										onInputChange={EMPTY_CALLBACK}
										size="m"
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_LAST_NAME
										}
									/>

									<MantineDateInput
										name={PERSONAL_INFO_FORM.BIRTH_DATE}
										maxDate={new Date()}
										label={FIELD_NAME.birthDate}
										labelProps={{ className: 'date-input-label' }}
										placeholder={renderPlaceHolderText(FIELD_NAME.birthDate)}
										size="md"
										readOnly={!isEditModeEnabled}
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_BIRTHDATE
										}
									/>

									<PhoneNumberInput
										name={PERSONAL_INFO_FORM.PHONE_NUMBER}
										phonePrefixName={PERSONAL_INFO_FORM.TELEPHONE_PREFIX}
										label={FIELD_NAME.phoneNumber}
										readOnly={!isEditModeEnabled}
										size="m"
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_PHONE_NUMBER
										}
									/>
								</PersonalInformationWrapper>
							</Grid.Col>
							<Grid.Col span={4}>
								<AddressWrapper>
									<Text variant="sans/11/800" disableVariantMapping>
										{tUserProfiles('personal_details.address')}
									</Text>

									<StringInputV2
										label={FIELD_NAME.houseNumber}
										readOnly={!isEditModeEnabled}
										name={PERSONAL_INFO_FORM.HOUSE_NUMBER}
										inputType="text"
										inputPlaceHolder={renderPlaceHolderText(
											FIELD_NAME.houseNumber
										)}
										onInputChange={EMPTY_CALLBACK}
										size="m"
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_HOUSE_NUMBER
										}
									/>

									<StringInputV2
										label={FIELD_NAME.street}
										readOnly={!isEditModeEnabled}
										name={PERSONAL_INFO_FORM.STREET}
										inputType="text"
										inputPlaceHolder={renderPlaceHolderText(FIELD_NAME.street)}
										onInputChange={EMPTY_CALLBACK}
										size="m"
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_STREET
										}
									/>

									<StringInputV2
										label={FIELD_NAME.city}
										readOnly={!isEditModeEnabled}
										name={PERSONAL_INFO_FORM.CITY}
										inputType="text"
										inputPlaceHolder={renderPlaceHolderText(FIELD_NAME.city)}
										onInputChange={EMPTY_CALLBACK}
										size="m"
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_CITY
										}
									/>
									<StyledSelect
										name={PERSONAL_INFO_FORM.COUNTRY}
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_COUNTRY
										}
										label={
											<LabelWrapper>
												<MantineText fz={11} fw={400}>
													{FIELD_NAME.country}
												</MantineText>
											</LabelWrapper>
										}
										placeholder={renderPlaceHolderText(FIELD_NAME.country)}
										data={COUNTRIES_GROUPED_OPTIONS}
										searchable
										size="md"
										disabled={!isEditModeEnabled}
										readOnly={!isEditModeEnabled}
										autoComplete="off"
									/>

									<StringInputV2
										label={FIELD_NAME.postCode}
										readOnly={!isEditModeEnabled}
										name={PERSONAL_INFO_FORM.POST_CODE}
										inputType="text"
										inputPlaceHolder={renderPlaceHolderText(
											FIELD_NAME.postCode
										)}
										onInputChange={EMPTY_CALLBACK}
										size="m"
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_POSTCODE
										}
									/>
								</AddressWrapper>
							</Grid.Col>
						</Grid>

						{!!isSignatureImageTurnedOn && (
							<Flex gap="1rem" direction="column" mt="1rem">
								<MantineText size="B6Bold">
									{tUserProfiles('personal_details.signature_title')}
								</MantineText>

								{savedSignature.isPresent ? (
									<PngPreview
										imageUrl={savedSignature.savedSignatureDataUrl ?? ''}
										style={{ height: '7rem', width: 'fit-content' }}
										withCheckeredBackground={false}
									/>
								) : (
									<MantineText size="B6Medium">
										{tUserProfiles('personal_details.signature_not_provided')}
									</MantineText>
								)}

								<Flex gap="0.5rem">
									<Button
										size="sm"
										variant="primary"
										onClick={() => setShowSignatureModal(true)}
										w="fit-content"
									>
										{savedSignature.isPresent
											? tUserProfiles('personal_details.add_new_signature')
											: tUserProfiles('personal_details.add_signature')}
									</Button>

									{savedSignature.isPresent && (
										<Button
											size="sm"
											variant="outline"
											color="red"
											loading={deleteSignature.isPending}
											w="fit-content"
											onClick={() => {
												if (
													window.confirm(
														tUserProfiles(
															'personal_details.delete_signature_confirm',
															{
																defaultValue:
																	'Are you sure you want to delete your signature?',
															}
														)
													)
												) {
													deleteSignature.mutate(undefined, {
														onSuccess: () =>
															showToastV2({
																title: tUserProfiles(
																	'personal_details.signature_deleted_title',
																	{
																		defaultValue:
																			'Signature deleted',
																	}
																),
																message: tUserProfiles(
																	'personal_details.signature_deleted_message',
																	{
																		defaultValue:
																			'Your signature has been removed.',
																	}
																),
															}),
														onError: () =>
															showToastV2({
																title: tUserProfiles(
																	'personal_details.signature_delete_failed_title',
																	{
																		defaultValue:
																			'Failed to delete signature',
																	}
																),
																message: tUserProfiles(
																	'personal_details.signature_delete_failed_message',
																	{
																		defaultValue:
																			'Please try again.',
																	}
																),
																state: 'error',
															}),
													});
												}
											}}
										>
											{tUserProfiles('personal_details.delete_signature', {
												defaultValue: 'Delete signature',
											})}
										</Button>
									)}
								</Flex>
							</Flex>
						)}
					</PersonalInputsWrapper>

					<FieldsDivider />

					{isEditModeEnabled && (
						<ButtonWrapper>
							<ButtonV2
								icon="save"
								leftIcon
								size="s"
								variant="primary"
								buttonType="submit"
								buttonTheme="light"
								disabled={!isDirty}
								data-testid={
									INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_SAVE_BUTTON
								}
							>
								<Text variant="sans/11/600" disableVariantMapping>
									{tCommonUi(`common_buttons.save`)}
								</Text>
							</ButtonV2>

							<ButtonV2
								icon="close-v3"
								leftIcon
								data-testid={
									INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_CANCEL_BUTTON
								}
								size="s"
								variant="tertiary"
								buttonTheme="light"
								onClick={() => {
									if (isDirty) {
										setShowModal(true);
									} else {
										changeToViewMode();
									}
								}}
							>
								<Text variant="sans/11/600" disableVariantMapping>
									{tCommonUi(`common_buttons.cancel`)}
								</Text>
							</ButtonV2>
						</ButtonWrapper>
					)}
					{!isEditModeEnabled && (
						<ButtonV2
							icon="pencil-edit-line"
							leftIcon
							data-testid={
								INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_PERSONAL_DETAILS_EDIT_BUTTON
							}
							buttonTheme="light"
							size="s"
							variant="tertiary"
							onClick={() => setIsEditModeEnabled(true)}
						>
							<Text variant="sans/11/600" disableVariantMapping>
								{tCommonUi(`common_buttons.edit`)}
							</Text>
						</ButtonV2>
					)}
				</form>
			</FormProvider>
			{showModal && (
				<CancelConfirmationModal
					className={className}
					onClose={() => {
						setShowModal(false);
					}}
					discardChange={changeToViewMode}
					texts={{
						title: tUserProfiles('personal_details.alert.title'),
						description: tUserProfiles('personal_details.alert.description'),
						buttonText: tUserProfiles('personal_details.alert.button_text'),
					}}
				/>
			)}
			{showSignatureModal && (
				<AddSignatureModal
					opened={showSignatureModal}
					onClose={() => setShowSignatureModal(false)}
					onConfirm={() => setShowSignatureModal(false)}
				/>
			)}
		</div>
	);
};
const ButtonWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
`;

const PersonalInformationWrapper = styled.div`
	display: flex;
	gap: 1rem;
	flex-direction: column;
`;
const AddressWrapper = styled.div`
	display: flex;
	gap: 1rem;
	flex-direction: column;
`;
const PersonalInputsWrapper = styled.div``;
const FieldsDivider = styled(Divider)`
	background-color: ${({ theme }) => theme.v2.color.neutral[200]};
	margin-top: 2rem;
	margin-bottom: 1rem;
`;
const LabelWrapper = styled.div``;
const StyledSelect = styled(Select)`
	&& .mantine-Select-input:disabled,
	&& .mantine-Select-input[data-disabled] {
		${({ readOnly }) =>
			readOnly &&
			css`
				${LabelWrapper} {
					margin-bottom: 0.25rem;
				}

				width: 100%;
				border: solid 1px transparent;
				color: ${(props) => props.theme.v3.colors.dark[8]};
				background-color: transparent;
				padding-left: 0;
				opacity: inherit;
			`};
	}
`;

const Styled = styled(ManagePersonalDetails)`
	.date-input-label {
		font-weight: 400;
	}
`;

export { Styled as ManagePersonalDetails };
